// /lib/cookies.js
const COOKIE_NAME = "lume_cookies_v1";
const COOKIE_MAX_AGE_DAYS = 180;

// Heuristic: domains where setting a parent-level cookie is disallowed (public suffix / shared hosts)
const PUBLIC_SUFFIX_LIKE = [
    // Cloudflare / Vercel / Netlify / GitHub / Heroku / AWS, etc.
    ".pages.dev",
    ".workers.dev",
    ".vercel.app",
    ".netlify.app",
    ".github.io",
    ".herokuapp.com",
    ".cloudfront.net",
    ".render.com",
    ".fly.dev",
];

/**
 * Return an apex domain we *own* (eTLD+1-ish) or null if we should not set Domain=.
 * This avoids trying to set cookies on public suffixes (e.g. .pages.dev), which browsers reject.
 */
function getSafeApexDomain() {
    try {
        const host = window.location.hostname; // e.g. shop.lumestore.pl or lumestore.pl
        if (!host || host === "localhost") return null;

        // IP address? Host-only cookie.
        if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;

        // If host matches a known public-suffix-like domain, don't try to set a parent domain cookie.
        if (PUBLIC_SUFFIX_LIKE.some(suffix => host.endsWith(suffix))) return null;

        const parts = host.split(".");
        if (parts.length < 2) return null;

        // Try "last two labels" as a pragmatic eTLD+1; works for most .pl/.com/.net/etc.
        // If you use multi-level country TLDs like .co.uk, consider hard-coding your real apex instead.
        const apex = parts.slice(-2).join(".");
        // If apex equals the host (already apex), still okay to return it — we'll set Domain=.apex for subdomains only.
        return apex;
    } catch {
        return null;
    }
}

function setCookie(baseParts, domain) {
    const parts = [...baseParts];
    if (domain) parts.push(`Domain=.${domain}`);
    document.cookie = parts.join("; ");
}

export function readConsentCookie() {
    try {
        const m = document.cookie.match(new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)"));
        if (!m) return null;
        return JSON.parse(decodeURIComponent(m[1]));
    } catch {
        return null;
    }
}

export function writeConsentCookie(value) {
    const json = encodeURIComponent(JSON.stringify(value));
    const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    const secure = window.location.protocol === "https:" ? "Secure" : null;

    const baseParts = [
        `${COOKIE_NAME}=${json}`,
        `Max-Age=${maxAge}`,
        "Path=/",
        "SameSite=Lax",
    ];
    if (secure) baseParts.push(secure);

    // Always set a host-only cookie so it works on preview/shared hosts (e.g., *.pages.dev)
    setCookie(baseParts, null);

    // Additionally set a parent-domain cookie if it's safe (for cross-subdomain persistence in production).
    const apex = getSafeApexDomain();
    if (apex && window.location.hostname.endsWith("." + apex)) {
        setCookie(baseParts, apex);
    }
}

export function deleteGaCookies() {
    try {
        const names = (document.cookie || "")
            .split(";")
            .map(c => c.trim().split("=")[0])
            .filter(n => /^_ga/.test(n));

        const host = window.location.hostname;
        const apex = getSafeApexDomain();
        const secure = window.location.protocol === "https:" ? "Secure" : null;

        const domainVariants = [
            null, // host-only
            host, // explicit host
            apex && host.endsWith("." + apex) ? apex : null, // safe apex if applicable
        ].filter(Boolean);

        names.forEach(name => {
            domainVariants.forEach(domain => {
                const parts = [
                    `${name}=`,
                    "Max-Age=0",
                    "Path=/",
                    "SameSite=Lax",
                ];
                if (secure) parts.push("Secure");
                if (domain && domain !== host) parts.push(`Domain=.${domain}`);
                document.cookie = parts.join("; ");
            });
        });
    } catch {
        /* ignore */
    }
}
